import type {
  InsightsOverviewResponse,
  SocialPlatform,
} from "@nlc/shared-types";

import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { SOCIAL_PLATFORM_LABELS } from "../../socialPlatformLabels";
import { formatMetric } from "../formatMetric";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const PLATFORM_LINES: Partial<
  Record<SocialPlatform, { color: string; dashed?: boolean }>
> = {
  facebook: { color: "var(--color-utility-orange-500)", dashed: true },
  instagram: { color: "var(--color-fg-brand-primary)" },
};

const compactFormat = new Intl.NumberFormat(undefined, {
  notation: "compact",
});

export function ViewsChart({
  daily,
  days,
  subtitle,
  timezone,
}: {
  daily: InsightsOverviewResponse["daily"];
  days: number;
  subtitle: string;
  timezone: string;
}) {
  const platforms = [...new Set(daily.map((entry) => entry.platform))];
  const today = safeToday(timezone);
  const data = Array.from({ length: days }, (_, index) => {
    const date = today.subtract(days - 1 - index, "day").format("YYYY-MM-DD");

    return {
      date,
      ...Object.fromEntries(
        platforms.map((platform) => [
          platform,
          daily.find(
            (entry) => entry.date === date && entry.platform === platform,
          )?.views ?? 0,
        ]),
      ),
    };
  });

  return (
    <div className="flex flex-col gap-lg p-xl">
      <div className="flex flex-col gap-xxs">
        <h3 className="text-lg font-semibold text-text-primary">
          Views over time
        </h3>
        <span className="text-sm text-text-tertiary">{subtitle}</span>
      </div>
      <div className="h-72">
        <ResponsiveContainer height="100%" width="100%">
          <LineChart
            data={data}
            margin={{ bottom: 0, left: 0, right: 8, top: 8 }}
          >
            <CartesianGrid
              stroke="var(--color-border-secondary)"
              vertical={false}
            />
            <XAxis
              axisLine={false}
              dataKey="date"
              fontSize={12}
              minTickGap={24}
              stroke="var(--color-text-tertiary)"
              tickFormatter={(date: string) => dayjs(date).format("MMM D")}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              fontSize={12}
              stroke="var(--color-text-tertiary)"
              tickFormatter={(value: number) => compactFormat.format(value)}
              tickLine={false}
              width={40}
            />
            <Tooltip
              formatter={(value: number, platform: string) => [
                formatMetric(value),
                SOCIAL_PLATFORM_LABELS[platform as SocialPlatform] ?? platform,
              ]}
              labelFormatter={(date: string) =>
                dayjs(date).format("MMM D, YYYY")
              }
            />
            <Legend
              align="right"
              formatter={(platform: string) =>
                SOCIAL_PLATFORM_LABELS[platform as SocialPlatform] ?? platform
              }
              iconType="plainline"
              verticalAlign="top"
            />
            {platforms.map((platform) => (
              <Line
                dataKey={platform}
                dot={false}
                key={platform}
                stroke={PLATFORM_LINES[platform]?.color}
                strokeDasharray={
                  PLATFORM_LINES[platform]?.dashed ? "6 4" : undefined
                }
                strokeWidth={2}
                type="monotone"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function safeToday(timezone: string) {
  try {
    return dayjs().tz(timezone).startOf("day");
  } catch {
    return dayjs().startOf("day");
  }
}
